import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Tiny strip showing the last N Resend send attempts at a glance.
 * Pulls from GET /api/admin/email-recent. Auto-refreshes every 60s.
 * Renders nothing if no attempts yet (clean state).
 */
export default function EmailHealthWidget({ limit = 5 }) {
  const [attempts, setAttempts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchAttempts = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/email-recent");
      setAttempts(r.data?.attempts || []);
    } catch (e) {
      setAttempts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttempts();
    const t = setInterval(fetchAttempts, 60000); // refresh every 60s
    return () => clearInterval(t);
  }, []);

  if (attempts === null) return null; // initial load – nothing yet
  if (attempts.length === 0) {
    return (
      <div data-testid="email-health" className="rounded-2xl border border-emerald-100 bg-white p-4 mb-6">
        <div className="flex items-center gap-2 text-emerald-800 font-[Outfit] text-sm">
          <span className="h-2 w-2 rounded-full bg-slate-300" />
          <span className="font-semibold">Email Health</span>
          <span className="text-slate-500">— no send attempts since backend restart</span>
        </div>
      </div>
    );
  }

  // Show most-recent first; cap to `limit` unless expanded
  const ordered = [...attempts].reverse();
  const visible = expanded ? ordered : ordered.slice(0, limit);
  const okCount = attempts.filter((a) => a.ok).length;
  const failCount = attempts.filter((a) => !a.ok).length;
  const lastFail = ordered.find((a) => !a.ok);

  // Overall pill state
  const allOk = failCount === 0;
  const pillCls = allOk
    ? "bg-emerald-100 text-emerald-800 border-emerald-300"
    : "bg-rose-100 text-rose-800 border-rose-300";

  const fmtTs = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    } catch {
      return "";
    }
  };

  return (
    <div data-testid="email-health" className="rounded-2xl border border-emerald-100 bg-white shadow-sm mb-6 overflow-hidden">
      {/* Header strip */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-emerald-50 bg-gradient-to-r from-emerald-50/60 via-white to-amber-50/40">
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold font-[Outfit] px-2.5 py-1 rounded-full border ${pillCls}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${allOk ? "bg-emerald-500 animate-pulse" : "bg-rose-500"}`} />
            {allOk ? "Healthy" : `${failCount} Failed`}
          </span>
          <span className="font-[Cormorant_Garamond] text-lg text-emerald-950">Email Health</span>
          <span className="text-xs text-emerald-900/60 font-[Outfit]">
            {okCount} sent · {failCount} failed · last {attempts.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="email-health-refresh"
            onClick={fetchAttempts}
            disabled={loading}
            className="text-[11px] font-[Outfit] font-semibold text-emerald-700 hover:text-emerald-900 disabled:text-slate-300"
          >
            {loading ? "↻ Refreshing…" : "↻ Refresh"}
          </button>
          {attempts.length > limit && (
            <button
              data-testid="email-health-expand"
              onClick={() => setExpanded((v) => !v)}
              className="text-[11px] font-[Outfit] font-semibold text-emerald-700 hover:text-emerald-900"
            >
              {expanded ? "Show less" : `Show all ${attempts.length}`}
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <ul className="divide-y divide-emerald-50">
        {visible.map((a, i) => (
          <li
            key={`${a.ts}-${i}`}
            data-testid={`email-health-row-${i}`}
            className={`flex items-start gap-3 px-5 py-2.5 text-sm font-[Outfit] ${a.ok ? "bg-white" : "bg-rose-50/40"}`}
          >
            <span className={`mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold shrink-0 ${a.ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-200 text-rose-800"}`}>
              {a.ok ? "✓" : "✕"}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="font-semibold text-emerald-950 truncate">{a.to}</span>
                <span className="text-[11px] text-emerald-900/50 tabular-nums">{fmtTs(a.ts)}</span>
              </div>
              {!a.ok && (
                <div className="text-xs text-rose-700 mt-0.5 line-clamp-2">{a.reason}</div>
              )}
              {a.ok && a.id && (
                <div className="text-[11px] text-emerald-700/60 mt-0.5 font-mono truncate">id: {a.id}</div>
              )}
            </div>
          </li>
        ))}
      </ul>

      {/* Footer hint when failures present */}
      {!allOk && lastFail && (
        <div className="px-5 py-2.5 bg-rose-50 border-t border-rose-100 text-xs text-rose-800 font-[Outfit]">
          💡 Most recent failure: <span className="font-semibold">{lastFail.to}</span> — check Resend dashboard or click <span className="font-mono bg-white px-1 py-0.5 rounded border border-rose-200">Send Replay Digests</span> to retry.
        </div>
      )}
    </div>
  );
}
